import { useCallback } from 'react';
import type { TaxGroup } from '@masamune/shared';
import { useAuthStore } from '../../../stores/auth.store';
import { useModalStore } from '../../../stores/modal.store';
import { Icon } from '../../../components/ui/Icon';
import { DesktopOnly } from '../../../components/ui/DesktopOnly';
import { FISCO_VISTE, VISTA_LABEL, type FiscoVista } from '../../../lib/adminRoutes';
import { canWriteAdmin } from '../adminApi';
import { AdminHeader, ViewTabs, useVista, type ViewTab } from '../shared/AdminShell';
import { VISTA_DI_GRUPPO } from './fiscoApi';
import { CalendarioView } from './CalendarioView';
import { VistaTipo } from './VistaTipo';
import { IvaView } from './IvaView';
import { RateView } from './RateView';
import '../admin.css';
import './fisco.css';

/**
 * Fisco (`/admin/fisco`): cosa dobbiamo allo Stato, per tipo.
 *
 * Viste (`?vista=`, le chiavi restano quelle dello scheletro):
 *   Calendario delle tasse (predefinita) · F24 dipendenti · IVA ·
 *   Imposte della società (IRES e IRAP) · Debiti a rate · Altri versamenti.
 * Ogni vista si apre con una frase calcolata dai dati, poi una fascia di
 * somme, la lista, le pagate in un blocco chiuso. I conti li fa l'API
 * (/admin/fisco, admin.fisco.ts).
 *
 * Niente filtro dello storico: le scadenze fiscali si vedono sempre, anche
 * quelle degli anni prima (SPEC, eccezioni alla data di taglio).
 * L'azione principale ("Nuova tassa da pagare") sta solo in intestazione.
 */
/** Le etichette stanno in lib/adminRoutes.ts (VISTA_LABEL): qui solo il tooltip. */
const TITOLI: Partial<Record<FiscoVista, string>> = {
  scadenze: 'Tutte le tasse per data: quelle non versate in tempo e quelle in arrivo',
  f24: 'Tasse e contributi trattenuti nelle buste paga',
  iva: "L'IVA da versare, trimestre per trimestre",
  imposte: "IRES allo Stato e IRAP alla Regione, sui conti dell'anno prima",
  rate: 'Debiti vecchi messi a posto con un piano a rate',
  altri: 'Ritenute dei collaboratori, Camera di commercio, libri sociali, imposta di registro',
};
const TABS: ViewTab<FiscoVista>[] = FISCO_VISTE.map((k) => ({ key: k, label: VISTA_LABEL.fisco[k], title: TITOLI[k] }));

export function FiscoPage() {
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteAdmin(user);
  const openModal = useModalStore((s) => s.open);
  const [vista, setVista] = useVista<FiscoVista>(FISCO_VISTE);
  const onVai = useCallback((g: TaxGroup) => {
    setVista(VISTA_DI_GRUPPO[g]);
    document.querySelector('.os-main')?.scrollTo?.({ top: 0, behavior: 'smooth' });
  }, [setVista]);

  return (
    <div className="page adm-page">
      <AdminHeader
        title="Fisco"
        sub="Le tasse da versare allo Stato, divise per tipo: quelle non versate in tempo e quelle in arrivo."
        primary={canWrite ? (
          <button className="btn btn-primary" onClick={() => openModal('newTax')}>
            <Icon name="plus" /> Nuova tassa da pagare
          </button>
        ) : undefined}
      />

      <ViewTabs tabs={TABS} active={vista} onChange={setVista} />

      {vista === 'scadenze' && <CalendarioView canWrite={canWrite} onVai={onVai} />}
      {vista === 'f24' && <VistaTipo group="F24_DIPENDENTI" canWrite={canWrite} onVai={onVai} />}
      {vista === 'iva' && <IvaView canWrite={canWrite} onVai={onVai} />}
      {vista === 'imposte' && <VistaTipo group="SOCIETA" canWrite={canWrite} onVai={onVai} />}
      {vista === 'rate' && <RateView canWrite={canWrite} onVai={onVai} />}
      {vista === 'altri' && <VistaTipo group="ALTRI" canWrite={canWrite} onVai={onVai} />}

      {/* Dal telefono si consulta e si segna pagato; creare una tassa nuova si fa dal computer (mobile.md § 8). */}
      {canWrite && vista === 'scadenze' && (
        <div className="m-only mt-6">
          <DesktopOnly
            cosa="Aggiungere una tassa da pagare"
            perche="servono importo, scadenza e da dove viene la cifra, da controllare sui documenti del commercialista. Dal telefono puoi segnare pagata una tassa che c'è già."
          >
            {null}
          </DesktopOnly>
        </div>
      )}
    </div>
  );
}
